import { useTranslations } from "@zapier/i18n";
import { Icon } from "@zapier/zinnia-icons";

import {
  BetaMenuTriggerItem,
  BetaMenuTriggerList,
  BetaMenuTriggerPopover,
  BetaMenuTriggerRoot,
  BetaMenuTriggerSeparator,
  Button,
  IconButton,
  Text,
  Tooltip,
} from "@zapier/design-system";

import {
  useEmitFolderActionEvent,
  FolderActionInteractionOption,
} from "@zapier/shared";
import { FolderTypeQueryState, isPersonalFolder } from "@zapier/utils";
import { useRef, useState } from "react";
import { FolderCreationModal } from "../FolderCreationModal";
import styles from "./CreationButton.module.css";
import {
  useAccountActionPermissions,
  useAgentsEntitlement,
  useFolderActionPermissions,
} from "@zapier/client";
import { useCreateAssetInFolder } from "../../hooks/useCreateAssetInFolder";

const MENU_ITEM_ZAP = "newZap";
const MENU_ITEM_TABLE = "newTable";
const MENU_ITEM_INTERFACE = "newInterface";
const MENU_ITEM_CHATBOT = "newChatbot";
const MENU_ITEM_CANVAS = "newCanvas";
const MENU_ITEM_AGENT = "newAgent";
const MENU_ITEM_FOLDER = "newFolder";

interface Props {
  createZapHref?: string;
  parentId?: string;
  parentName?: string;
  parentIsShared?: boolean;
  folderDepth?: number;
}

export function CreationButton({
  createZapHref,
  parentId,
  parentName,
  parentIsShared,
  folderDepth,
}: Props) {
  const t = useTranslations("folder-creation-button");

  const [isFolderCreationModalOpen, setIsFolderCreationModalOpen] =
    useState(false);
  const createButtonReference = useRef<HTMLButtonElement>(null);
  const mobileCreateButtonReference = useRef<HTMLButtonElement>(null);
  const folderCreationOpenerReference = useRef<HTMLButtonElement | null>(null);

  const getVisibleCreateButton = () => {
    const mobileButton = mobileCreateButtonReference.current;
    if (mobileButton && mobileButton.offsetParent !== null) {
      return mobileButton;
    }
    return createButtonReference.current ?? mobileButton;
  };

  const handleFolderCreationOpenChange = (open: boolean) => {
    if (open) {
      folderCreationOpenerReference.current = getVisibleCreateButton();
    }

    setIsFolderCreationModalOpen(open);

    if (!open) {
      const opener = folderCreationOpenerReference.current;
      requestAnimationFrame(() => opener?.focus());
    }
  };
  const { emit } = useEmitFolderActionEvent();
  const { isSharingEnabled, isChatbotsEnabled } = useAccountActionPermissions();
  const { canCreateZap, canCreateSubfolder, canCreateSubfolderLimit } =
    useFolderActionPermissions({
      folderId: parentId,
      folderDepth,
    });

  const { enabled: isAgentsEnabled } = useAgentsEntitlement();

  const {
    createTable,
    createInterface,
    createChatbot,
    createCanvas,
    createAgent,
    isCreating,
  } = useCreateAssetInFolder({
    folderId: parentId ?? "",
  });

  const isInPersonalFolder =
    parentId !== undefined && isPersonalFolder(parentId);

  const allowCreateZap = parentId ? canCreateZap : true;
  const allowCreateFolder = parentId
    ? canCreateSubfolder && canCreateSubfolderLimit
    : true;

  const showAssetCreationOptions = Boolean(parentId) && !isInPersonalFolder;

  const handleAction = (key: React.Key) => {
    switch (key) {
      case MENU_ITEM_ZAP: {
        if (allowCreateZap && createZapHref) {
          globalThis.open(createZapHref, "_blank");
        }
        break;
      }
      case MENU_ITEM_TABLE: {
        emit({
          optionSelected: FolderActionInteractionOption.CreateTable,
          interactionRawText: parentId ?? "",
        });
        createTable();
        break;
      }
      case MENU_ITEM_INTERFACE: {
        emit({
          optionSelected: FolderActionInteractionOption.CreateInterface,
          interactionRawText: parentId ?? "",
        });
        createInterface();
        break;
      }
      case MENU_ITEM_CHATBOT: {
        emit({
          optionSelected: FolderActionInteractionOption.CreateChatbot,
          interactionRawText: parentId ?? "",
        });
        createChatbot();
        break;
      }
      case MENU_ITEM_CANVAS: {
        emit({
          optionSelected: FolderActionInteractionOption.CreateCanvas,
          interactionRawText: parentId ?? "",
        });
        createCanvas();
        break;
      }
      case MENU_ITEM_AGENT: {
        emit({
          optionSelected: FolderActionInteractionOption.CreateAgent,
          interactionRawText: parentId ?? "",
        });
        createAgent();
        break;
      }
      case MENU_ITEM_FOLDER: {
        handleFolderCreationOpenChange(true);
        emit({
          optionSelected: FolderActionInteractionOption.CreateFolder,
          interactionRawText: FolderTypeQueryState.Personal,
        });
        break;
      }
    }
  };

  const newFolderLabel = parentId
    ? t("menu-items.new-subfolder")
    : t("menu-items.new-folder");

  const renderMenu = () => (
    <BetaMenuTriggerPopover>
      <BetaMenuTriggerList
        aria-label={t("accessibility.creation-options-label")}
        onAction={handleAction}
      >
        <BetaMenuTriggerItem
          id={MENU_ITEM_ZAP}
          textValue={t("menu-items.zap")}
          isDisabled={!allowCreateZap}
        >
          <span
            className={`${styles.menuText} ${!allowCreateZap && styles.disabledItem}`}
          >
            <Icon
              name="miscBoltAltFill"
              size="20"
              color={allowCreateZap ? "GrayWarm6" : "GrayWarm5"}
            />
            <Text type="Body1Multiline">{t("menu-items.zap")}</Text>
          </span>
        </BetaMenuTriggerItem>

        {showAssetCreationOptions && (
          <>
            <BetaMenuTriggerItem
              id={MENU_ITEM_TABLE}
              textValue={t("menu-items.table")}
              isDisabled={isCreating}
            >
              <span
                className={`${styles.menuText} ${isCreating && styles.disabledItem}`}
              >
                <Icon
                  name="navTables"
                  size="20"
                  color={isCreating ? "GrayWarm5" : "GrayWarm6"}
                />
                <Text type="Body1Multiline">{t("menu-items.table")}</Text>
              </span>
            </BetaMenuTriggerItem>

            <BetaMenuTriggerItem
              id={MENU_ITEM_INTERFACE}
              textValue={t("menu-items.interface")}
              isDisabled={isCreating}
            >
              <span
                className={`${styles.menuText} ${isCreating && styles.disabledItem}`}
              >
                <Icon
                  name="navInterfaces"
                  size="20"
                  color={isCreating ? "GrayWarm5" : "GrayWarm6"}
                />
                <Text type="Body1Multiline">{t("menu-items.interface")}</Text>
              </span>
            </BetaMenuTriggerItem>

            <BetaMenuTriggerItem
              id={MENU_ITEM_CHATBOT}
              textValue={t("menu-items.chatbot")}
              isDisabled={isCreating || !isChatbotsEnabled}
            >
              <span
                className={`${styles.menuText} ${(isCreating || !isChatbotsEnabled) && styles.disabledItem}`}
              >
                <Icon
                  name="navAIChatbot"
                  size="20"
                  color={
                    isCreating || !isChatbotsEnabled ? "GrayWarm5" : "GrayWarm6"
                  }
                />
                <Text type="Body1Multiline">{t("menu-items.chatbot")}</Text>
              </span>
            </BetaMenuTriggerItem>

            <BetaMenuTriggerItem
              id={MENU_ITEM_CANVAS}
              textValue={t("menu-items.canvas")}
              isDisabled={isCreating}
            >
              <span
                className={`${styles.menuText} ${isCreating && styles.disabledItem}`}
              >
                <Icon
                  name="navCanvas"
                  size="20"
                  color={isCreating ? "GrayWarm5" : "GrayWarm6"}
                />
                <Text type="Body1Multiline">{t("menu-items.canvas")}</Text>
              </span>
            </BetaMenuTriggerItem>

            <BetaMenuTriggerItem
              id={MENU_ITEM_AGENT}
              textValue={t("menu-items.agent")}
              isDisabled={isCreating || !isAgentsEnabled}
            >
              <span
                className={`${styles.menuText} ${(isCreating || !isAgentsEnabled) && styles.disabledItem}`}
              >
                <Icon
                  name="navAgents"
                  size="20"
                  color={
                    isCreating || !isAgentsEnabled ? "GrayWarm5" : "GrayWarm6"
                  }
                />
                <Text type="Body1Multiline">{t("menu-items.agent")}</Text>
              </span>
            </BetaMenuTriggerItem>
          </>
        )}

        {!isInPersonalFolder && (
          <>
            <BetaMenuTriggerSeparator />

            <BetaMenuTriggerItem
              id={MENU_ITEM_FOLDER}
              textValue={newFolderLabel}
              isDisabled={!allowCreateFolder}
            >
              <span
                className={`${styles.menuText} ${!allowCreateFolder && styles.disabledItem}`}
              >
                <Icon
                  name="navFolder"
                  size="20"
                  color={allowCreateFolder ? "GrayWarm6" : "GrayWarm5"}
                />
                <Text
                  type="Body1Multiline"
                  color={allowCreateFolder ? "GrayWarm9" : "GrayWarm5"}
                >
                  {newFolderLabel}
                </Text>
              </span>
            </BetaMenuTriggerItem>
          </>
        )}
      </BetaMenuTriggerList>
    </BetaMenuTriggerPopover>
  );

  return (
    <div className={styles.root}>
      <div className={styles.iconButtonWrapper}>
        <Tooltip
          content={t("accessibility.toggle-create-options-menu")}
          placement="bottom"
        >
          <BetaMenuTriggerRoot>
            <IconButton
              ref={mobileCreateButtonReference}
              aria-label={t("accessibility.toggle-create-actions-menu")}
              name="formAdd"
              id="create-actions-button-mobile"
              size="medium"
              variant="ghost"
            />
            {renderMenu()}
          </BetaMenuTriggerRoot>
        </Tooltip>
      </div>

      <div className={styles.regularButtonWrapper}>
        <BetaMenuTriggerRoot>
          <Button
            ref={createButtonReference}
            variant="primary"
            id="create-actions-button"
            size="medium"
            iconPosition="start"
            isPending={isCreating}
          >
            <Icon name="formAdd" />
            {t("buttons.create")}
          </Button>
          {renderMenu()}
        </BetaMenuTriggerRoot>
      </div>

      <FolderCreationModal
        isOpen={isFolderCreationModalOpen}
        setIsOpen={handleFolderCreationOpenChange}
        isSharedSelectable={!parentId && isSharingEnabled}
        parentId={parentId}
        targetParentFolderName={parentName}
        isShared={parentIsShared}
      />
    </div>
  );
}
